import * as React from 'react';
import { ErrorCode, type FileError } from 'react-dropzone';

import type { FileUploaderProps } from 'src/components/FileUploader/FileUploader';
import { FileModel } from 'src/components/FileUploader/fileModel';
import { getDefaultAlertErrorMessage } from 'src/components/FileUploader/fileUploaderDefaultErrorMessages';

/**
 * An internal React hook that encapsulates the `FileUploader` component's
 * handling of custom and default "top level" errors displayed in an `Alert`.
 *
 * @access package
 */
export function useFileUploaderAlertErrors(
  ownerProps: FileUploaderAlertErrorsHookOwnerProps
): FileUploaderAlertErrorsHookRet {
  const {
    acceptedMimeTypes,
    maxNumberOfFiles,
    minSizeInBytes,
    maxSizeInBytes,
  } = ownerProps;

  const getDefaultAlertErrors = React.useCallback<
    (latestFileModels: FileModel[]) => FileError[]
  >(
    (latestFileModels) => {
      let alertErrors = //
        latestFileModels
          .map(({ errors = [] }) => {
            return errors.map((err) => {
              const errorCode = err.code;
              const message = getDefaultAlertErrorMessage({
                errorCode,
                acceptedMimeTypes,
                maxNumberOfFiles,
                minSizeInBytes,
                maxSizeInBytes,
                fileValues: latestFileModels,
              });
              return message
                ? {
                    code: errorCode,
                    message,
                  }
                : undefined;
            });
          })
          .flat()
          // If message is undefined, it means that this message should not be displayed
          // as a top level alert, so we will exclude those.
          .filter((err): err is FileError => err !== undefined);

      const thereAreTooManyFiles =
        // 0 stands for "inifinite" in DropzoneOptions
        maxNumberOfFiles === 0
          ? false
          : latestFileModels.length > maxNumberOfFiles;
      const alertErrorAboutTooManyFilesWillBeDisplayed = alertErrors.some(
        (e) => e.code === ErrorCode.TooManyFiles
      );

      // Validate consumer's controlled errors to save them if they misconfigure / provide bad persisted data.
      if (thereAreTooManyFiles && !alertErrorAboutTooManyFilesWillBeDisplayed) {
        // A top-level "too many files" message should be displayed to the user,
        // but the prop configuration won't produce one by default.
        const message = getDefaultAlertErrorMessage({
          errorCode: ErrorCode.TooManyFiles,
          acceptedMimeTypes,
          maxNumberOfFiles,
          minSizeInBytes,
          maxSizeInBytes,
          fileValues: latestFileModels,
        });
        if (message) {
          alertErrors.push({
            code: ErrorCode.TooManyFiles,
            message,
          });
        }
      } else if (
        !thereAreTooManyFiles &&
        alertErrorAboutTooManyFilesWillBeDisplayed
      ) {
        // A top-level "too many files" message should not be displayed to the user,
        // but the prop configuration will produce one by default.
        alertErrors = alertErrors.filter(
          (err) => err.code !== ErrorCode.TooManyFiles
        );
      }

      return alertErrors;
    },
    [acceptedMimeTypes, maxNumberOfFiles, maxSizeInBytes, minSizeInBytes]
  );

  return {
    getDefaultAlertErrors,
  };
}

type FileUploaderAlertErrorsHookOwnerProps = Required<
  Pick<FileUploaderProps, 'acceptedMimeTypes' | 'maxNumberOfFiles'>
> &
  Pick<FileUploaderProps, 'minSizeInBytes' | 'maxSizeInBytes'>;

type FileUploaderAlertErrorsHookRet = {
  /**
   * Returns the default `alertErrors` that should be sent to the consumer's onChange
   * callback function.
   */
  getDefaultAlertErrors: (
    latestFileModels: FileUploaderProps['fileValues']
  ) => FileError[];
};

export default useFileUploaderAlertErrors;
